import React, { ReactNode, useEffect, useState } from "react"
import EventEmitter from 'events'
import { AuthContextInterface, usePromiseLoader } from "@surveying-hub-bv/fe-component-library"
import { MainContentWrapper, PageWrapper } from "./ContentWrappers"
import { HeaderWithSidebar } from "./Header"
import { SideMenu } from "./Sidemenu"
import { NavItem } from "./NavigationMap"
import { useLocation } from "react-router-dom"
import { TokenService } from "../../services/TokenService"
import { ExtendedAuthContextInterface } from "../../AuthProvider"
import { SubscriptionService } from "../../services/SubscriptionService"
import { CheckoutService } from "../../services/CheckoutService"
import { SEO } from "../../components/SEO"
import { MetaService } from "../../services/MetaService"
import { TrackingService } from "../../services/TrackingService"


function useIsMobile(breakpoint = 768) {
    const query = `(max-width:${breakpoint}px)`
    const [match, setMatch] = useState(() => window.matchMedia(query).matches)

    useEffect(() => {
        const mql = window.matchMedia(query)
        const handler = () => setMatch(mql.matches)
        mql.addEventListener("change", handler)
        return () => mql.removeEventListener("change", handler)
    }, [query])

    return match
}

export type LayoutProps = {
    callBack?: EventEmitter
    pageName?: string
    topLogo: ReactNode
    copyRight?: ReactNode
    selector?: ReactNode[]
    loggedIn?: ExtendedAuthContextInterface
    navigation: NavItem[]
    accountPageUrl: string
    loginUrl: string | (() => void)
    profileType?: string
} & InjectableProps
export const Layout = ({ callBack, topLogo, pageName, main, loggedIn, accountPageUrl, loginUrl, navigation, copyRight, selector, profileType }: LayoutProps) => {
    const isMobile = useIsMobile()
    const location = useLocation()
    const storageKey = "layout-sidemenu-open"

    // initial open-state: only pull from storage on desktop
    const [isOpen, setIsOpen] = useState<boolean>(() => {
        if (isMobile) return false
        const saved = localStorage.getItem(storageKey)
        return saved ? JSON.parse(saved) : false
    })

    /* ─────────────  persist desktop state  ───────────── */
    useEffect(() => {
        if (!isMobile) {
            localStorage.setItem(storageKey, JSON.stringify(isOpen))
        }
    }, [isOpen, isMobile])

    /* ─────────────  close on route change (mobile)  ───────────── */
    useEffect(() => {
        if (isMobile) setIsOpen(false);
        //  runs whenever pathname OR hash/search changes
    }, [location.key, isMobile]);        // location.key updates on every navigation

    /* ─────────────  close when switching to mobile  ───────────── */
    useEffect(() => {
        if (isMobile) setIsOpen(false)
    }, [isMobile])

    /* ─────────────  toggle helper  ───────────── */
    const toggleSidemenu = () => {
        setIsOpen(prev => !prev);
        document.getElementById("sidemenu-drawer")?.scrollTo({ top: 0 })
    }

    /* ─────────────  Credits / Tokens  ───────────── */

    // Product pricing table
    const tokenCheckout = usePromiseLoader(async () => {
        if (!loggedIn) throw new Error("User not logged in");
        return TokenService.createTokenCheckoutSession(await loggedIn.getToken());
    })

    const createCheckoutSession = async (priceId: string) => {
        if (!loggedIn) {
            throw new Error("User is not logged in")
        }
        return CheckoutService.createProductCheckoutSession(await loggedIn.getToken(), priceId)
    }

    const fetchUserOverview = async () => {
        if (!loggedIn) {
            throw new Error("User is not logged in");
        }
        const overview = await SubscriptionService.getSubscriptions(await loggedIn.getToken());
        return {
            tokenAmount: overview.tokenAmount
        };
    }

    useEffect(() => {
        // Example async function to fetch GTM ID
        const fetchGtmId = async (): Promise<string | null> => {
            // If branding.isRebranded is true, do not return GTM ID
            if (!loggedIn) return null;
            return MetaService.getAppMeta()
                .then(response => {
                    if (response.gtagID) {
                        return response.gtagID
                    }
                    return null
                })
        }

        const injectGtm = (gtmId: string | null) => {
            if (!gtmId) return

            // Prevent duplicate injection
            if (!document.getElementById('gtm-script')) {
                // GTM script
                const script = document.createElement('script')
                script.id = 'gtm-script'
                script.innerHTML = `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtmId}');
        `
                document.head.appendChild(script)
            }

            // Inject GTM noscript iframe into body if not already present
            if (!document.getElementById('gtm-noscript')) {
                const noscript = document.createElement('noscript')
                noscript.id = 'gtm-noscript'
                noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
                document.body.insertBefore(noscript, document.body.firstChild)
            }
        }

        fetchGtmId().then(gtmId => {
            if (gtmId) {
                injectGtm(gtmId)
            }
        })
    }, [loggedIn])

    const reportOpenTokenPricingTable = () => {
        TrackingService.reportOpenTokenPricingTable();
    }

    return (
        <PageWrapper>

            {/* SEO Component to insure we always have default meta data on pages not specifically set. */}
            <SEO />

            {/* Sidemenu */}
            <SideMenu navigation={navigation} loginUrl={loginUrl} isOpen={isOpen} setIsOpen={toggleSidemenu} loggedIn={loggedIn} isLoggedIn={!!loggedIn} selector={selector ?? []} accountPageUrl={accountPageUrl} isMobile={isMobile} profileType={profileType} tokenCheckout={tokenCheckout.loader} reportOpenTokenPricingTable={reportOpenTokenPricingTable} createCheckoutSession={createCheckoutSession} fetchUserOverview={fetchUserOverview} />

            {/* TODO: the header may need the loggedIn and loggedInURL depending on where we tell the customer to log in. Currently, the idea is to do that on the sidemenu */}
            {/* Header */}
            <HeaderWithSidebar isOpen={isOpen} setIsOpen={toggleSidemenu} topLogo={topLogo} loginUrl={loginUrl} isLoggedIn={!!loggedIn} isMobile={isMobile} />

            {/* Main Content */}
            <MainContentWrapper sideMenuIsOpen={isOpen} isMobile={isMobile} isFullScreen={!!main.isFullscreen}>
                {main.content}
            </MainContentWrapper>
            {/* 
            <Navigation loggedIn={loggedIn} loginUrl={loginUrl} selector={selector} navigation={navigation} accountPageUrl={accountPageUrl} /> */}
        </PageWrapper>)
}

export type InjectableProps = { main: { content: ReactNode, isFullscreen?: boolean }, callBack?: EventEmitter, pageName?: string, loggedIn?: AuthContextInterface, }
export type LayoutComponent = React.FC<InjectableProps>
export const withLayout = (topLogo: ReactNode, navigation: NavItem[], accountPageUrl: string, loginUrl: string, pageName?: string, copyRight?: ReactNode) =>
    ({ main, callBack }: InjectableProps) => {
        return <Layout main={main} callBack={callBack} pageName={pageName} topLogo={topLogo} copyRight={copyRight} navigation={navigation} accountPageUrl={accountPageUrl} loginUrl={loginUrl} />
    }
